// src/components/FooterB2B.js
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Container,
  Stack,
  Link as MLink,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import { getPrimaryColor } from "../GlobalFunction/Theme";
import { useNavigate } from "react-router-dom";
import { getLangFromCookie, LANG_COOKIE } from "../../Utils/LangCookie";
import ProtectedImage from "../GlobalFunction/ProtectedImage";

const PLAY_URL = "https://play.google.com/store";
const APPSTORE_URL = "https://apps.apple.com";

const getCurrentLang = () =>
  getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";

const labelByLang = (item, lang) =>
  lang === "EN"
    ? item.EN || item.label
    : lang === "JP"
      ? item.JP || item.label
      : item.label;

const STR = {
  ZH: {
    downloadApp: "下載 App",
    qrHint:
      "掃描 QR 自動開啟對應商店（Android → Google Play；iOS → App Store）",
    switchToB2C: "SEHO LIFE 商城",
    switchToB2B: "切換 B2B",
  },
  EN: {
    downloadApp: "Download App",
    qrHint:
      "Scan the QR to open the right store (Android → Google Play; iOS → App Store).",
    switchToB2C: "Switch to B2C",
    switchToB2B: "Switch to B2B",
  },
  JP: {
    downloadApp: "アプリをダウンロード",
    qrHint:
      "QRをスキャンすると対応ストアが開きます（Android → Google Play／iOS → App Store）。",
    switchToB2C: "B2Cへ切替",
    switchToB2B: "B2Bへ切替",
  },
};

const topLinks = [
  { label: "PRO", EN: "PRO", JP: "プロ", href: "/" },
  { label: "LIFE", EN: "LIFE", JP: "ライフ", href: "/b2c" },
];

const infoLinksB2B = [
  {
    label: "網站地圖",
    EN: "Site Map",
    JP: "サイトマップ",
    href: "/網站地圖",
  },
  {
    label: "聯絡水禾",
    EN: "Contact Us",
    JP: "お問い合わせ",
    href: "/聯絡水禾",
  },
  {
    label: "隱私權",
    EN: "Privacy & Terms",
    JP: "プライバシーと利用規約",
    href: "/隱私權",
  },
   {
    label: "緊急聯繫",
    EN: "Emergency Contact",
    JP: "緊急連絡先",
    href: "/線上報修",
  },
];

const snsB2B = [
  {
    alt: "twitter/x",
    href: "https://twitter.com",
    src: "/img/icon/twitter.png",
  },
  {
    alt: "facebook",
    href: "https://facebook.com",
    src: "/img/icon/fb.png",
  },
  { alt: "line", href: "https://line.me", src: "/img/icon/line.png" },
  {
    alt: "instagram",
    href: "https://instagram.com",
    src: "/img/icon/ig.png",
  },
];

export default function FooterB2B({ mode, setMode, fonts }) {
  const navigate = useNavigate();
  const [qrOpen, setQrOpen] = useState(false);
  const [lang, setLang] = useState(getCurrentLang());

  useEffect(() => {
    const onChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onChange);
    return () => window.removeEventListener("app:languageChanged", onChange);
  }, []);

  const handleToggle = () => {
    setMode(mode === "b2b" ? "b2c" : "b2b");
    navigate(mode === "b2b" ? "/b2c" : "");
  };

  const qrUrl = useMemo(() => `${window.location.origin}/download-app`, []);
  const qrImg = useMemo(
    () =>
      `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(
        qrUrl,
      )}`,
    [qrUrl],
  );

  const L = STR[lang] || STR.ZH;

  // ✅ FIX: fonts prop might be undefined after App.js update
  const DEFAULT_FONTS = useMemo(
    () => ({
      EN: "LINE Seed Sans",
      ZH: "LINE Seed TW",
      JP: "LINE Seed JP",
    }),
    [],
  );

  const fontFamily = useMemo(() => {
    const key = String(lang || "ZH").toUpperCase();
    return (fonts && fonts[key]) || DEFAULT_FONTS[key] || DEFAULT_FONTS.ZH;
  }, [fonts, lang, DEFAULT_FONTS]);

  // group links into 3-per-column sets (for mobile columns)
  const chunk3 = (arr) =>
    Array.from({ length: Math.ceil(arr.length / 3) }, (_, i) =>
      arr.slice(i * 3, i * 3 + 3),
    );
  const infoCols = useMemo(() => chunk3(infoLinksB2B), []);

  return (
    <Box component="footer" sx={{ mt: "auto" }}>
      {/* ========== UPPER (teal) ========== */}
      <Box sx={{ bgcolor: getPrimaryColor("b2b") }}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            py: { xs: 1.5, md: 3.5 },
            "& > .inner": {
              width: "100%",
              maxWidth: "none",
              mx: 0,
              pl: { xs: 2, md: 3 },
              pr: { xs: 2, md: 6, lg: 8 },
            },
          }}
        >
          <Grid
            container
            className="inner"
            alignItems="center"
            wrap="nowrap"
            sx={{
              display: "grid",
              gridTemplateAreas: {
                xs: `"links"`,
                sm: `"links"`,
                md: `"logo links"`,
              },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr",
                md: "1fr auto",
              },
              rowGap: { xs: 0, sm: 0, md: 0 },
              columnGap: { xs: 0, sm: 0, md: 3 },
              justifyItems: { xs: "center", sm: "center", md: "stretch" },
            }}
          >
            {/* Logo */}
            <Box
              onClick={() => navigate("")}
              sx={{
                gridArea: "logo",
                cursor: "pointer",
                display: { xs: "none", sm: "none", md: "block" },
                justifySelf: { xs: "center", md: "start" },
              }}
            >
              <ProtectedImage
                src="/img/Logo/seho-white.png"
                alt="seho logo"
                ratio="5 / 1"
                position="center"
                sx={{
                  width: { xs: 200, sm: 250, md: 300 },
                  maxHeight: { xs: 48, sm: 56, md: 72 },
                }}
              />
            </Box>

            {/* Links */}
            <Box
              sx={{
                gridArea: "links",
                textAlign: "center",
                width: {
                  xs: "100%",
                  sm: "100%",
                  md: "auto",
                },
                justifySelf: {
                  xs: "center",
                  sm: "center",
                  md: "end",
                },
                minWidth: 0,
              }}
            >
              {/* Desktop row layout */}
              {/* Tablet and desktop row layout */}
              <Stack
                direction="row"
                spacing={{
                  sm: 3,
                  md: 3.5,
                  lg: 4,
                }}
                flexWrap="nowrap"
                justifyContent="center"
                alignItems="center"
                useFlexGap
                sx={{
                  display: { xs: "none", sm: "flex" },
                  width: "auto",
                  maxWidth: "100%",
                  textAlign: "center",
                  overflow: "visible",
                  "& a": { my: 0 },
                }}
              >
                {infoLinksB2B.map((l) => (
                  <MLink
                    key={l.label}
                    href={l.href}
                    underline="none"
                    color="#fff"
                    sx={{
                      fontSize: { xs: 13, sm: 15, md: 17, lg: 18 },
                      fontWeight: 400,
                      fontFamily,
                      letterSpacing: 0.5,
                      fontStyle: "italic",
                      lineHeight: 1.6,
                      whiteSpace: "nowrap",
                      minWidth: "max-content",
                      flexShrink: 0,
                      overflow: "visible",
                      textOverflow: "clip",
                      transition: "color .18s ease",
                      WebkitTapHighlightColor: "rgba(0,0,0,0)",
                      "&:hover, &:focus-visible, &:active": {
                        color: getPrimaryColor("other"),
                      },
                    }}
                  >
                    {labelByLang(l, lang)}
                  </MLink>
                ))}
              </Stack>

              {/* Mobile-only 2 x 2 centered layout */}
              <Box
                sx={{
                  display: { xs: "grid", sm: "none" },
                  gridTemplateColumns: "repeat(2, max-content)",
                  justifyContent: "center",
                  alignItems: "center",
                  columnGap: 2.25,
                  rowGap: 0.75,
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {infoLinksB2B.map((l) => (
                  <MLink
                    key={l.label}
                    href={l.href}
                    underline="none"
                    color="#fff"
                    sx={{
                      textAlign: "center",
                      fontSize: 17,
                      fontWeight: 400,
                      fontStyle: "italic",
                      fontFamily,
                      letterSpacing: 0.5,
                      lineHeight: 1.6,
                      whiteSpace: "nowrap",
                      minWidth: "max-content",
                      flexShrink: 0,
                      overflow: "visible",
                      textOverflow: "clip",
                      transition: "color .18s ease",
                      WebkitTapHighlightColor: "rgba(0,0,0,0)",
                      "&:hover, &:focus-visible, &:active": {
                        color: getPrimaryColor("other"),
                      },
                    }}
                  >
                    {labelByLang(l, lang)}
                  </MLink>
                ))}
              </Box>
            </Box>

            {/* SNS + smartphone icons */}
            <Box
              sx={{
                gridArea: "sns",
                width: "100%",
                visibility: "hidden",
                display: { xs: "none", sm: "none", md: "none" },
                justifyContent: { xs: "space-between", md: "flex-end" },
                alignItems: "center",
                gap: { xs: 0, md: 1.75 },
              }}
            >
              {snsB2B.map((s) => (
                <MLink
                  key={s.alt}
                  href={s.href}
                  target="_blank"
                  rel="noopener"
                  aria-label={s.alt}
                  sx={{
                    lineHeight: 0,
                    display: "inline-flex",
                  }}
                >
                  <Box
                    component="img"
                    src={s.src}
                    alt={s.alt}
                    sx={{
                      width: { xs: 52, md: 44 },
                      height: { xs: 52, md: 44 },
                      display: "block",
                      transition: "opacity .2s ease",
                      "&:hover": { opacity: 0.75 },
                    }}
                  />
                </MLink>
              ))}

              <IconButton
                aria-label="Download app"
                onClick={() => setQrOpen(true)}
                disableRipple
                sx={{
                  width: { xs: 52, md: 44 },
                  height: { xs: 52, md: 44 },
                  p: 0,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  color: getPrimaryColor("b2b"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "& svg": {
                    fontSize: { xs: 30, md: 30 },
                  },
                  transition: "opacity .2s ease",
                  "&:hover": {
                    opacity: 0.75,
                    backgroundColor: "#fff",
                  },
                }}
              >
                <SmartphoneIcon />
              </IconButton>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* ========== LOWER (white copyright) ========== */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            py: 1.25,
            "& > .inner": {
              width: "100%",
              maxWidth: "none",
              mx: 0,
              pl: { xs: 2, md: 3 },
              pr: { xs: 2, md: 6, lg: 8 },
            },
          }}
        >
          <Box
            className="inner"
            sx={{
              display: "flex",
              justifyContent: {
                xs: "center",
                sm: "center",
                md: "flex-end",
              },
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                width: "auto",
                minWidth: "max-content",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  lineHeight: 1.6,
                  whiteSpace: "nowrap",
                }}
              >
                Copyright©2025 台灣水禾實業有限公司
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ========== QR Dialog ========== */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5 }}>
          {L.downloadApp}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ textAlign: "center", p: 1 }}>
            <Box
              component="img"
              src={qrImg}
              alt="App QR"
              sx={{
                width: 260,
                height: 260,
                display: "block",
                mx: "auto",
                mb: 1,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {L.qrHint}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AndroidIcon />}
            onClick={() => window.open(PLAY_URL, "_blank")}
          >
            Google Play
          </Button>
          <Button
            variant="outlined"
            startIcon={<AppleIcon />}
            onClick={() => window.open(APPSTORE_URL, "_blank")}
          >
            App Store
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
